const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/** @type {Set<HTMLElement>} */
const activeModals = new Set();

/**
 * @param {File} file
 * @param {number} maxBytes
 */
function validateImageFile(file, maxBytes) {
  if (!file.type.startsWith('image/') && !/\.heic$/i.test(file.name)) {
    return 'Please upload an image file (JPG, PNG, HEIC, etc.).';
  }
  if (file.size > maxBytes) {
    return `Image must be under ${Math.round(maxBytes / (1024 * 1024))} MB.`;
  }
  return null;
}

/**
 * @param {File} file
 * @param {number} maxBytes
 */
async function compressImage(file, maxBytes) {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    const maxDim = 1200;
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    let quality = 0.82;
    let blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    while (blob && blob.size > maxBytes && quality > 0.35) {
      quality -= 0.08;
      blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    }

    if (blob) {
      return new File([blob], 'room-photo.jpg', { type: 'image/jpeg' });
    }
  } catch {
    /* fall through */
  }

  return file;
}

/**
 * @param {Response} response
 */
async function formspreeRejectedFiles(response) {
  if (response.status !== 400) return false;
  try {
    const data = await response.clone().json();
    const message = JSON.stringify(data).toLowerCase();
    return message.includes('file upload');
  } catch {
    return false;
  }
}

/**
 * @param {string} endpoint
 * @param {FormData} payload
 */
async function postToFormspree(endpoint, payload) {
  return fetch(endpoint, {
    method: 'POST',
    body: payload,
    headers: { Accept: 'application/json' },
  });
}

/**
 * @param {HTMLElement} root
 */
function initHelpMeStyle(root) {
  if (root.dataset.helpMeStyleInit === 'true') return;
  root.dataset.helpMeStyleInit = 'true';

  const dialog = root.querySelector('dialog');
  const form = root.querySelector('[data-help-me-style-form]');
  const dropzone = root.querySelector('[data-help-me-style-dropzone]');
  const fileInput = root.querySelector('[data-help-me-style-file]');
  const preview = root.querySelector('[data-help-me-style-preview]');
  const previewImage = root.querySelector('[data-help-me-style-preview-image]');
  const removeButton = root.querySelector('[data-help-me-style-remove]');
  const errorEl = root.querySelector('[data-help-me-style-error]');
  const successEl = root.querySelector('[data-help-me-style-success]');
  const openButtons = root.querySelectorAll('[data-help-me-style-open]');
  const closeButtons = root.querySelectorAll('[data-help-me-style-close]');

  if (!(dialog instanceof HTMLDialogElement) || !(form instanceof HTMLFormElement)) return;

  const maxBytes = Number(root.dataset.maxFileBytes) || MAX_FILE_BYTES;
  const slackWebhook = root.dataset.slackWebhook || '';
  const submissionEndpoint = root.dataset.submissionEndpoint || '';

  /** @type {File | null} */
  let selectedFile = null;

  const showError = (message) => {
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  };

  const setPreview = (file) => {
    selectedFile = file;
    if (!(preview instanceof HTMLElement) || !(previewImage instanceof HTMLImageElement)) return;

    if (!file) {
      preview.hidden = true;
      previewImage.removeAttribute('src');
      return;
    }

    previewImage.src = URL.createObjectURL(file);
    preview.hidden = false;
    showError('');
  };

  const openModal = () => {
    if (dialog.open) return;
    dialog.showModal();
    activeModals.add(root);
    showError('');
  };

  const closeModal = () => {
    if (!dialog.open) return;
    dialog.close();
    activeModals.delete(root);
  };

  const handleFile = (file) => {
    if (!file) return;
    const error = validateImageFile(file, maxBytes);
    if (error) {
      showError(error);
      return;
    }
    setPreview(file);
  };

  openButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      openModal();
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      closeModal();
    });
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeModal();
  });

  dialog.addEventListener('close', () => {
    activeModals.delete(root);
  });

  if (dropzone instanceof HTMLElement) {
    dropzone.addEventListener('click', () => fileInput?.click());

    dropzone.addEventListener('dragover', (event) => {
      event.preventDefault();
      dropzone.classList.add('help-me-style__dropzone--active');
    });

    dropzone.addEventListener('dragleave', () => {
      dropzone.classList.remove('help-me-style__dropzone--active');
    });

    dropzone.addEventListener('drop', (event) => {
      event.preventDefault();
      dropzone.classList.remove('help-me-style__dropzone--active');
      const file = event.dataTransfer?.files?.[0];
      handleFile(file ?? null);
    });
  }

  if (fileInput instanceof HTMLInputElement) {
    fileInput.addEventListener('change', () => {
      handleFile(fileInput.files?.[0] ?? null);
    });
  }

  if (removeButton instanceof HTMLButtonElement) {
    removeButton.addEventListener('click', (event) => {
      event.preventDefault();
      if (fileInput instanceof HTMLInputElement) fileInput.value = '';
      setPreview(null);
    });
  }

  document.addEventListener('paste', (event) => {
    if (!activeModals.has(root)) return;

    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        handleFile(item.getAsFile());
        break;
      }
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    showError('');

    if (!selectedFile) {
      showError('Please paste or upload a photo of your room.');
      return;
    }

    const formData = new FormData(form);
    const email = String(formData.get('email') || '').trim();
    const intent = String(formData.get('intent') || '').trim();

    if (!email) {
      showError('Please enter your email address.');
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    const productTitle = root.dataset.productTitle || '';
    const productUrl = root.dataset.productUrl || '';

    if (!submissionEndpoint) {
      showError('Submissions are not set up yet. Please try again later.');
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
      return;
    }

    const payload = new FormData();
    payload.append('email', email);
    payload.append('intent', intent);
    payload.append('product_title', productTitle);
    payload.append('product_url', productUrl);

    const compressed = await compressImage(selectedFile, Math.min(maxBytes, 400 * 1024));
    payload.append('room_photo', compressed, compressed.name || 'room-photo.jpg');

    try {
      let response = await postToFormspree(submissionEndpoint, payload);

      if (await formspreeRejectedFiles(response)) {
        /* Formspree free plan blocks files — send a compressed link instead */
        const reader = new FileReader();
        const dataUrl = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = reject;
          reader.readAsDataURL(compressed);
        });

        if (dataUrl.length > 450000) {
          showError('Photo is too large. Try a smaller image or crop your screenshot.');
          if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
          return;
        }

        const textPayload = new FormData();
        textPayload.append('email', email);
        textPayload.append('intent', intent);
        textPayload.append('product_title', productTitle);
        textPayload.append('product_url', productUrl);
        textPayload.append('room_photo_link', dataUrl);

        response = await postToFormspree(submissionEndpoint, textPayload);
      }

      if (!response.ok) {
        throw new Error('Submission failed');
      }
    } catch {
      showError('Something went wrong sending your photo. Please try again.');
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
      return;
    }

    if (slackWebhook) {
      try {
        await fetch(slackWebhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: [
              '*New Help me style request*',
              `*Product:* ${productTitle || 'Unknown'}`,
              productUrl ? `<${productUrl}|View product>` : null,
              `*Email:* ${email}`,
              intent ? `*Intent:* ${intent}` : null,
              '*Room photo:* received — check your Formspree inbox (email notification).',
            ]
              .filter(Boolean)
              .join('\n'),
          }),
        });
      } catch {
        /* Slack is optional; submission already saved */
      }
    }

    form.hidden = true;
    if (successEl instanceof HTMLElement) successEl.hidden = false;
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
  });
}

document.querySelectorAll('[data-help-me-style-root]').forEach((root) => {
  if (root instanceof HTMLElement) initHelpMeStyle(root);
});

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  event.target.querySelectorAll('[data-help-me-style-root]').forEach((root) => {
    if (root instanceof HTMLElement) initHelpMeStyle(root);
  });
});
